// Serverpaginerte lister over raddokumenter (docs/architecture/row-per-document-scaling-plan.md,
// S4). For et foretak med raddomener (stateRows.js) ligger hver faktura og betaling som eget
// dokument med en `list`-projeksjon serveren beregner ved skriving
// (functions/accounting/invoiceListProjection.ts). En side kan da lese en filtrert, sortert
// og paginert spørring i stedet for hele arrayet: likhetsfiltre på `kind`, `list.status`
// (én eller flere), `partyId`, `open` og `list.archived`, et datointervall, prefikssøk på
// nummer (`list.search`) eller partnavn (`list.party`), forfalt/ikke forfalt på `list.dueDate`,
// nyeste først, markørpaginering, og `count()`/`sum()` for tallene over lista. Bare gjeldende
// side har en lytter.
//
// Én ulikhet per spørring: søk eller forfall eier den, og da filtreres datointervallet på
// klienten per side (tellingen er da ikke eksakt, og `aggregate` sier fra med `exact`).
//
// Indeksene står i firestore.indexes.json. Uten markør finnes ingen rader, og siden bruker
// arraymodellen som før (`available` sier fra). Lastes lett sammen med sidene som bruker den.

(function () {
  const DEFAULT_PAGE_SIZE = 50;
  const MAX_PAGE_SIZE = 200;
  // Web-SDK-en (compat) tillater 10 verdier i et `in`-filter.
  const MAX_STATUSES = 10;

  const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const text = (value) => String(value ?? '').trim();
  const isoDate = (value) => {
    const raw = text(value);
    const iso = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (iso) return iso[1];
    const nb = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
    return nb ? `${nb[3]}-${nb[2]}-${nb[1]}` : '';
  };
  // Samme dagsgrense som invoiceStatusToUi: forfallsdatoen (midnatt UTC) er passert.
  const todayIso = () => new Date().toISOString().slice(0, 10);

  const available = (appDocData, domain = 'sales') => Boolean(window.PlummeStateRows?.activeRowDomains?.(appDocData)?.includes(domain));

  // Normaliserer filtrene til det Firestore-spørringen kan uttrykke.
  const normalizeFilters = (filters = {}) => {
    const search = text(filters.search);
    const statuses = [...new Set((Array.isArray(filters.statuses) ? filters.statuses : [filters.status]).map(text).filter(Boolean))].slice(0, MAX_STATUSES);
    const due = filters.due === 'overdue' || filters.due === 'current' ? filters.due : null;
    return {
      kind: text(filters.kind),
      statementId: text(filters.statementId),
      statuses,
      partyId: text(filters.partyId).toLowerCase(),
      open: typeof filters.open === 'boolean' ? filters.open : null,
      archived: typeof filters.archived === 'boolean' ? filters.archived : null,
      // Forfall gjelder bare sendte dokumenter; med andre statuser i tillegg filtreres det per side.
      due: due && statuses.length === 1 && statuses[0] === 'sent' ? due : null,
      dueSplit: due,
      from: isoDate(filters.from),
      to: isoDate(filters.to),
      search: /^id:/i.test(search) ? '' : search.toLowerCase(),
      ...(/^id:/i.test(search) ? { documentId: search.slice(3).trim() } : {}),
      today: isoDate(filters.today) || todayIso(),
      pageSize: Math.min(MAX_PAGE_SIZE, Math.max(1, Number(filters.pageSize) || DEFAULT_PAGE_SIZE)),
    };
  };

  // Et søk som begynner med et siffer er et dokumentnummer; alt annet er et partnavn.
  const searchField = (search) => (/^\d/.test(search) ? 'list.search' : 'list.party');
  // Ulikheten spørringen bruker: 'search', 'due' eller 'date'.
  const inequalityOf = (filters) => (filters.search ? 'search' : filters.due ? 'due' : 'date');

  const buildQuery = (collection, filters) => {
    let query = collection;
    if (filters.statementId) return query.where('statementId', '==', filters.statementId).orderBy('position', 'asc');
    if (filters.kind) query = query.where('kind', '==', filters.kind);
    if (filters.statuses.length === 1) query = query.where('list.status', '==', filters.statuses[0]);
    else if (filters.statuses.length > 1) query = query.where('list.status', 'in', filters.statuses);
    if (filters.partyId) query = query.where('partyId', '==', filters.partyId);
    if (filters.open !== null) query = query.where('open', '==', filters.open);
    // Arkivfilteret og søket deler ikke indeks; under søk filtreres arkivet per side.
    if (filters.archived !== null && !filters.search) query = query.where('list.archived', '==', filters.archived);
    const inequality = inequalityOf(filters);
    if (inequality === 'search') {
      // Prefikssøk: [search, search + høyeste tegn). Sorteres på søkefeltet, dato på klienten.
      const field = searchField(filters.search);
      return query.where(field, '>=', filters.search).where(field, '<', filters.search + '').orderBy(field);
    }
    if (inequality === 'due') {
      return (filters.due === 'overdue' ? query.where('list.dueDate', '<=', filters.today) : query.where('list.dueDate', '>', filters.today)).orderBy('list.dueDate', 'desc');
    }
    if (filters.from) query = query.where('date', '>=', filters.from);
    if (filters.to) query = query.where('date', '<=', filters.to);
    return query.orderBy('date', 'desc');
  };

  const rowsOf = (snapshot) => (snapshot?.docs || []).map((doc) => ({ id: doc.id, ...(doc.data() || {}) }))
    .filter((entry) => isPlainObject(entry) && 'row' in entry);

  const inDateRange = (entry, filters) => (!filters.from || (entry.date || '') >= filters.from) && (!filters.to || (entry.date || '') <= filters.to);
  const isOverdue = (entry, filters) => entry?.list?.status === 'sent' && Boolean(entry.list.dueDate) && entry.list.dueDate <= filters.today && entry.kind !== 'offer';
  // Det spørringen ikke kunne uttrykke, filtreres per side her.
  const matchesLocally = (entry, filters) => {
    if ('documentId' in filters) {
      if (entry.id !== filters.documentId || entry.row?.id !== filters.documentId
        || (filters.statementId && entry.statementId !== filters.statementId)
        || (filters.kind && entry.kind !== filters.kind)
        || (filters.statuses.length && !filters.statuses.includes(entry.list?.status))
        || (filters.partyId && entry.partyId !== filters.partyId)
        || (filters.open !== null && entry.open !== filters.open)
        || (filters.archived !== null && Boolean(entry.list?.archived) !== filters.archived)
        || !inDateRange(entry, filters)) return false;
      return !filters.dueSplit || entry.list?.status !== 'sent'
        || (filters.dueSplit === 'overdue' ? isOverdue(entry, filters) : !isOverdue(entry, filters));
    }
    if (inequalityOf(filters) !== 'date' && !inDateRange(entry, filters)) return false;
    if (filters.search && filters.archived !== null && Boolean(entry?.list?.archived) !== filters.archived) return false;
    // Search owns the query inequality even for a single sent status. Apply the
    // due split locally whenever the actual query did not apply it.
    if (filters.dueSplit && inequalityOf(filters) !== 'due' && entry?.list?.status === 'sent') return filters.dueSplit === 'overdue' ? isOverdue(entry, filters) : !isOverdue(entry, filters);
    return true;
  };
  // Tellingen er eksakt når spørringen uttrykker alle filtrene selv.
  const isExact = (filters) => (inequalityOf(filters) === 'date' || (!filters.from && !filters.to)) && !(filters.dueSplit && inequalityOf(filters) !== 'due') && !(filters.search && filters.archived !== null);

  // Tall over lista: `count()` for filteret, og `sum(field)` når SDK-en har aggregatet
  // (compat 10.x: `query.aggregate` + `firebase.firestore.AggregateField.sum`); ellers null.
  const aggregate = (businessRef, collectionId, filters = {}, { sum = '' } = {}) => {
    const normalized = normalizeFilters(filters);
    if ('documentId' in normalized) {
      const ref = documentRef(businessRef.collection(collectionId), normalized.documentId);
      return (ref ? ref.get() : Promise.resolve(null)).then(snapshot => {
        const rows = documentRows(snapshot, normalized);
        const value = rows[0] && sum.split('.').reduce((entry, key) => entry?.[key], rows[0]);
        return { count: rows.length, sum: sum ? (typeof value === 'number' && Number.isFinite(value) ? value : 0) : null, exact: true };
      });
    }
    const query = buildQuery(businessRef.collection(collectionId), normalized);
    const count = typeof query.count === 'function'
      ? query.count().get().then((snapshot) => Number(snapshot.data()?.count ?? 0))
      : Promise.resolve(null);
    const sumField = typeof globalThis.firebase?.firestore?.AggregateField?.sum === 'function' && typeof query.aggregate === 'function'
      ? globalThis.firebase.firestore.AggregateField.sum : null;
    const total = sum && sumField
      ? query.aggregate({ total: sumField(sum) }).get().then((snapshot) => Number(snapshot.data()?.total ?? 0))
      : Promise.resolve(null);
    return Promise.all([count, total]).then(([rows, value]) => ({ count: rows, sum: value, exact: isExact(normalized) }));
  };

  // A document lookup stays within the supplied business collection. Never pass
  // a user-supplied path to doc(), or silently turn an invalid ID into a scan.
  const documentRef = (collection, id) => id && id !== '.' && id !== '..' && !id.includes('/')
    && !/^__.*__$/.test(id) && new TextEncoder().encode(id).length <= 1500 ? collection.doc(id) : null;
  const documentRows = (snapshot, filters) => rowsOf({ docs: snapshot?.exists ? [snapshot] : [] })
    .filter(entry => matchesLocally(entry, filters));
  const createDocumentList = ({ businessRef, collectionId, onChange, onError }, filters) => {
    const ref = documentRef(businessRef.collection(collectionId), filters.documentId);
    let entries = [], loading = false, total = null, version = 0, generation = 0, stopped = false, unsubscribe;
    const emit = () => { if (!stopped) { version++; onChange?.(); } };
    const load = () => {
      if (stopped) return;
      const request = ++generation;
      unsubscribe?.(); entries = []; total = null; loading = Boolean(ref);
      if (!ref) { total = 0; emit(); return; }
      unsubscribe = ref.onSnapshot(snapshot => {
        if (stopped || request !== generation) return;
        entries = documentRows(snapshot, filters); total = entries.length; loading = false; emit();
      }, error => { if (!stopped && request === generation) { entries = []; total = null; loading = false; onError?.(error); emit(); } });
    };
    load();
    return {
      rows: () => entries, total: () => total, exact: () => true, hasMore: () => false,
      complete: () => total !== null,
      loading: () => loading, version: () => version, loadMore: () => Promise.resolve(false), reload: load,
      filters: () => ({ ...filters }),
      stop() { stopped = true; generation++; unsubscribe?.(); entries = []; },
    };
  };

  // Retain one page and one cursor, never a growing history of visited pages.
  // Snapshot cursors include document identity, so equal sort values remain stable.
  const createWindowedList = (base, filters, onChange, onError) => {
    let docs = [], total = null, loading = false, failed = false, stopped = false;
    let previous = false, next = false, version = 0, generation = 0, countGeneration = 0, unsubscribe;
    let request = { direction: 'first', cursor: null, inclusive: false };
    const emit = () => { if (!stopped) { version++; onChange?.(); } };
    const listen = (target) => {
      if (stopped) return;
      const delivery = ++generation;
      unsubscribe?.();
      request = target; docs = []; loading = true; failed = false;
      previous = next = false;
      let query = base;
      if (target.cursor) {
        const method = target.direction === 'next'
          ? (target.inclusive ? 'startAt' : 'startAfter') : (target.inclusive ? 'endAt' : 'endBefore');
        query = query[method](target.cursor);
      }
      query = target.direction === 'previous' ? query.limitToLast(filters.pageSize + 1) : query.limit(filters.pageSize + 1);
      emit();
      unsubscribe = query.onSnapshot(snapshot => {
        if (stopped || delivery !== generation) return;
        const extra = snapshot.docs.length > filters.pageSize;
        docs = target.direction === 'previous' ? snapshot.docs.slice(-filters.pageSize) : snapshot.docs.slice(0, filters.pageSize);
        previous = target.direction === 'previous' ? extra : target.direction === 'next';
        next = target.direction === 'previous' || extra;
        loading = false; failed = false; emit();
      }, error => {
        if (stopped || delivery !== generation) return;
        docs = []; total = null; previous = next = false; loading = false; failed = true;
        onError?.(error); emit();
      });
    };
    const count = () => {
      const delivery = ++countGeneration;
      total = null;
      if (typeof base.count !== 'function') return;
      base.count().get().then(snapshot => {
        if (!stopped && delivery === countGeneration && !failed) { total = Number(snapshot.data()?.count ?? 0); emit(); }
      }).catch(error => { if (!stopped && delivery === countGeneration) onError?.(error); });
    };
    const navigate = (direction) => {
      if (stopped || loading || failed || !(direction === 'next' ? next : previous)) return Promise.resolve(false);
      // If concurrent deletion leaves a page empty, its incoming cursor still
      // lets the user return, including the boundary document if it still exists.
      const cursor = (direction === 'next' ? docs.at(-1) : docs[0]) || request.cursor;
      if (!cursor) return Promise.resolve(false);
      listen({ direction, cursor, inclusive: docs.length === 0 });
      return Promise.resolve(true);
    };
    const reload = () => { if (!stopped) { listen({ direction: 'first', cursor: null, inclusive: false }); count(); } };
    reload();
    return {
      windowed: true,
      rows: () => rowsOf({ docs }).filter(entry => matchesLocally(entry, filters)),
      total: () => total, exact: () => isExact(filters), loading: () => loading, error: () => failed,
      hasMore: () => next, hasPrevious: () => previous, version: () => version,
      complete: () => !loading && !failed && !previous && !next,
      loadMore: () => navigate('next'), loadPrevious: () => navigate('previous'), reload,
      retry: () => { if (!stopped) { listen(request); count(); } },
      filters: () => ({ ...filters }),
      stop() { stopped = true; generation++; countGeneration++; unsubscribe?.(); unsubscribe = null; docs = []; request = { direction: 'first', cursor: null, inclusive: false }; },
    };
  };

  // Legacy consumers retain loaded pages; opt-in consumers use a bounded window.
  const createRowList = ({ businessRef, collectionId, filters = {}, onChange, onError, windowed = false }) => {
    const normalized = normalizeFilters(filters);
    if ('documentId' in normalized) return createDocumentList({ businessRef, collectionId, onChange, onError }, normalized);
    const collection = businessRef.collection(collectionId);
    const base = buildQuery(collection, normalized);
    if (windowed) return createWindowedList(base, normalized, onChange, onError);
    let unsubscribe = null;
    let pages = []; // [{ rows, last }] i rekkefølge; siste side har lytteren
    let total = null;
    let hasMore = true;
    let loading = false;
    let stopped = false;
    let generation = 0;
    let listenerVersion = 0;
    let version = 0; // øker ved hver levering, så en mottaker kan huske avledninger per levering

    // A changed sort key can move a document across a frozen page boundary.
    // Prefer its newest delivered version and never render duplicate identities.
    const rows = () => [...new Map(pages.flatMap((page) => page.rows).map((entry) => [entry.id, entry])).values()]
      .filter((entry) => matchesLocally(entry, normalized));
    const emit = () => { if (!stopped) { version += 1; onChange?.(); } };
    const listen = (query, index) => {
      const delivery = ++listenerVersion;
      if (unsubscribe) unsubscribe();
      unsubscribe = query.onSnapshot(
        (snapshot) => {
          if (stopped || delivery !== listenerVersion) return;
          const pageRows = rowsOf(snapshot);
          pages[index] = { rows: pageRows, last: snapshot.docs[snapshot.docs.length - 1] || pages[index]?.last || null };
          hasMore = snapshot.docs.length >= normalized.pageSize;
          loading = false;
          emit();
        },
        (error) => { if (!stopped && delivery === listenerVersion) { loading = false; onError?.(error); emit(); } },
      );
    };
    const countTotal = () => {
      const request = generation;
      if (typeof base.count !== 'function') return Promise.resolve(null);
      return base.count().get().then((snapshot) => {
        if (stopped || request !== generation) return null;
        total = Number(snapshot.data()?.count ?? 0); emit(); return total;
      }).catch((error) => { if (!stopped && request === generation) onError?.(error); return null; });
    };

    const load = () => {
      if (stopped) return;
      generation += 1;
      loading = true;
      hasMore = true;
      total = null;
      pages = [];
      listen(base.limit(normalized.pageSize), 0);
      countTotal();
    };
    const loadMore = () => {
      if (stopped) return Promise.resolve(false);
      const last = pages[pages.length - 1]?.last;
      if (!hasMore || loading || !last) return Promise.resolve(false);
      loading = true;
      // Lytteren flyttes til den nye siden; de forrige sidene beholdes som sist levert.
      listen(base.startAfter(last).limit(normalized.pageSize), pages.length);
      return Promise.resolve(true);
    };
    load();
    return {
      rows, // () => rader (dokumenter med `row` og `list`)
      total: () => total,
      exact: () => isExact(normalized),
      hasMore: () => hasMore,
      loading: () => loading,
      version: () => version,
      loadMore,
      reload: load,
      filters: () => ({ ...normalized }),
      stop() { stopped = true; listenerVersion += 1; generation += 1; if (unsubscribe) unsubscribe(); unsubscribe = null; pages = []; },
    };
  };

  window.PlummeRowLists = { DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE, available, normalizeFilters, buildQuery, rowsOf, matchesLocally, aggregate, createRowList };
})();
